const _excluded = ["style", "className", "children", "variant", "size", "renderIcon", "renderRightIcon", "disabled", "alert", "loading", "selected", "type"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import styledC from 'styled-components';
import { useDeprecate, variantSwitch } from '@adsk/alloy-react-helpers';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, { getInteractiveStyles, stylePropType } from '@adsk/alloy-react-theme';
import { buttonTypePropType } from './consts';
import BasicButton from './templates/BasicButton';
const BUTTON_VARIANTS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary'
};
const BUTTON_SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium'
};
const HiddenDiv = styledC.div.withConfig({
  displayName: "Button__HiddenDiv",
  componentId: "sc-1n24ikq-0"
})(["opacity:0;"]);

/**
 * A button component which wraps an HTML button and adds to it the basic "btn" bootstrap class.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to HTML button element.
 */
const Button = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
      style,
      className,
      children,
      variant = BUTTON_VARIANTS.SECONDARY,
      size = BUTTON_SIZES.MEDIUM,
      renderIcon,
      renderRightIcon,
      disabled = false,
      alert = false,
      loading = false,
      selected = false,
      type
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  useDeprecate(!!props.width, 'Button', 'width', 'style');
  const disabledOrLoading = disabled || loading;
  const marginStyle = children ? {
    marginRight: theme.spacing.XS
  } : undefined;
  const textOnly = children && !renderIcon;
  const iconOnly = renderIcon && !children;
  const iconAndText = children && renderIcon;
  const iconStyle = variantSwitch({
    statement: variant,
    variants: {
      [BUTTON_VARIANTS.PRIMARY]: _objectSpread({}, disabled && {
        color: theme.tokens.colors.icon.inactive.value
      }),
      [BUTTON_VARIANTS.SECONDARY]: _objectSpread(_objectSpread({
        color: theme.tokens.colors.icon.secondary.value
      }, alert && {
        color: theme.tokens.colors.icon.error.bold.value
      }), disabled && {
        color: theme.tokens.colors.icon.inactive.value
      })
    }
  });

  // If loading and no renderIcon, we need to render the children to show the correct sized button
  // If there's a render icon, we change the icon to a progress ring
  const content = loading && !renderIcon ? /*#__PURE__*/React.createElement(HiddenDiv, null, children) : children;
  return /*#__PURE__*/React.createElement(BasicButton, _extends({
    type: type,
    tabIndex: disabled ? -1 : undefined
  }, props, {
    className: className,
    ref: ref,
    disabled: disabledOrLoading,
    "aria-disabled": disabledOrLoading,
    "aria-pressed": selected,
    style: [getInteractiveStyles(disabledOrLoading), {
      fontWeight: 600
    }, variantSwitch({
      statement: variant,
      variants: {
        [BUTTON_VARIANTS.PRIMARY]: _objectSpread(_objectSpread(_objectSpread({
          backgroundColor: theme.tokens.colors.background.brand.value,
          border: '1px solid transparent',
          color: theme.tokens.colors.text.inverse.value,
          '&:active': _objectSpread(_objectSpread({
            backgroundColor: theme.tokens.colors.background.brand.pressed.value,
            borderColor: 'transparent'
          }, loading && {
            background: theme.tokens.colors.background.brand.inactive.value
          }), disabled && {
            background: theme.tokens.colors.background.neutral.inactive.value
          })
        }, loading && {
          background: theme.tokens.colors.background.brand.inactive.value,
          cursor: 'auto'
        }), alert && _objectSpread(_objectSpread({
          background: theme.tokens.colors.background.error.value,
          color: theme.tokens.colors.text.error.inverse.value,
          '&:active': {
            backgroundColor: theme.tokens.colors.background.error.bold.value
          }
        }, loading && {
          background: theme.tokens.colors.background.error.inactive.value
        }), selected && !disabled && {
          background: theme.tokens.colors.background.error.selected.value
        })), disabled && {
          background: theme.tokens.colors.background.neutral.inactive.value,
          color: theme.tokens.colors.text.inactive.value
        }),
        [BUTTON_VARIANTS.SECONDARY]: _objectSpread(_objectSpread(_objectSpread(_objectSpread({
          border: `1px solid ${theme.tokens.colors.border.value}`,
          color: theme.tokens.colors.text.primary.value,
          '&:active': _objectSpread({}, !disabled && {
            borderColor: theme.tokens.colors.border.pressed.value
          })
        }, alert && {
          color: theme.tokens.colors.text.error.value
        }), disabled && {
          borderColor: theme.tokens.colors.border.inactive.value,
          color: theme.tokens.colors.text.inactive.value
        }), loading && {
          cursor: 'auto',
          color: theme.tokens.colors.text.inactive.value,
          borderColor: theme.tokens.colors.border.inactive.value
        }), selected && !disabled && {
          background: theme.tokens.colors.background.neutral.selected.value,
          '&:hover': {
            boxShadow: 'none'
          },
          '&:focus': {
            boxShadow: 'none'
          },
          '&:active': {
            boxShadow: 'none'
          }
        }),
        [BUTTON_VARIANTS.TERTIARY]: _objectSpread(_objectSpread(_objectSpread(_objectSpread({
          border: '1px solid transparent',
          color: theme.tokens.colors.text.link.value,
          '&:hover': _objectSpread({}, !disabled && {
            boxShadow: 'none',
            background: theme.tokens.colors.background.neutral.hover.value
          }),
          '&:focus': {
            boxShadow: theme.halos.focus,
            background: 'none'
          },
          '&:active': _objectSpread({}, !disabled && {
            borderColor: 'transparent',
            boxShadow: 'none',
            background: theme.tokens.colors.background.neutral.pressed.value
          })
        }, alert && {
          color: theme.tokens.colors.text.error.value
        }), disabled && {
          color: theme.tokens.colors.text.inactive.value
        }), loading && {
          cursor: 'auto',
          color: theme.tokens.colors.text.inactive.value,
          background: 'transparent'
        }), selected && !disabled && {
          background: theme.tokens.colors.background.neutral.selected.value,
          color: theme.tokens.colors.text.link.value,
          '&:hover': {
            boxShadow: 'none',
            background: theme.tokens.colors.background.neutral.hover.value
          },
          '&:focus': {
            boxShadow: 'none',
            background: theme.tokens.colors.background.neutral.selected.value
          },
          '&:active': {
            boxShadow: 'none',
            background: theme.tokens.colors.background.neutral.selected.value
          }
        })
      }
    }), variantSwitch({
      statement: size,
      variants: {
        [BUTTON_SIZES.SMALL]: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, theme.typography.labelSmall), {}, {
          height: 24
        }, textOnly && {
          padding: `0 7px`
        }), iconOnly && {
          padding: `0 3px`
        }), iconAndText && {
          padding: `0 7px 0 5px`
        }),
        [BUTTON_SIZES.MEDIUM]: _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, theme.typography.labelMedium), {}, {
          height: 36
        }, textOnly && {
          padding: `0 15px`
        }), iconOnly && {
          padding: `0 5px`
        }), iconAndText && {
          padding: `0 15px 0 11px`
        })
      }
    }), style]
  }), loading && renderIcon && /*#__PURE__*/React.createElement(ProgressRing, {
    style: marginStyle,
    variant: variant === BUTTON_VARIANTS.PRIMARY ? ProgressRing.VARIANTS.ALTERNATIVE : ProgressRing.VARIANTS.DEFAULT,
    size: size === BUTTON_SIZES.SMALL ? ProgressRing.SIZES.XSMALL : ProgressRing.SIZES.SMALL
  }), loading && !renderIcon && /*#__PURE__*/React.createElement(ProgressRing, {
    style: {
      position: 'absolute'
    },
    variant: variant === BUTTON_VARIANTS.PRIMARY ? ProgressRing.VARIANTS.ALTERNATIVE : ProgressRing.VARIANTS.DEFAULT,
    size: size === BUTTON_SIZES.SMALL ? ProgressRing.SIZES.XSMALL : ProgressRing.SIZES.SMALL
  }), !loading && (renderIcon === null || renderIcon === void 0 ? void 0 : renderIcon({
    size: size === BUTTON_SIZES.SMALL ? 16 : 24,
    style: [marginStyle, iconStyle]
  })), content, renderRightIcon === null || renderRightIcon === void 0 ? void 0 : renderRightIcon({
    size: size === BUTTON_SIZES.SMALL ? 16 : 24,
    style: children ? {
      marginLeft: theme.spacing.XS
    } : undefined
  }));
});
Button.displayName = 'Button';
Button.propTypes = {
  /** Button's content */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** button html type, eg: submit */
  type: buttonTypePropType,
  /** Disable button functionality */
  disabled: PropTypes.bool,
  /** handle onclick event for button */
  onClick: PropTypes.func,
  /** render prop to display an icon on the left */
  renderIcon: PropTypes.func,
  /** render prop to display an icon on the right */
  renderRightIcon: PropTypes.func,
  /**  The size of the button, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /** Deprecated: Adjust width using style instead */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**  The style variant, defaults to 'secondary' */
  variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
  /** alert mode */
  alert: PropTypes.bool,
  /** is loading state is on */
  loading: PropTypes.bool,
  /** is selected state is on */
  selected: PropTypes.bool
};
export default Object.assign(Button, {
  VARIANTS: BUTTON_VARIANTS,
  SIZES: BUTTON_SIZES
});
//# sourceMappingURL=Button.js.map